import { Injectable } from '@angular/core';
import { Auth, authState, EmailAuthProvider, reauthenticateWithCredential } from '@angular/fire/auth';
import { map, take } from 'rxjs';


@Injectable({
  providedIn: 'root'
})
export class AuthService {
  getUserEmail() {
    return this.auth.currentUser?.email;
  }

  constructor(public auth: Auth,) {

    authState(this.auth).pipe().subscribe(isLoggedIn => {
      //console.log("isLoggedIn", isLoggedIn);
    });
  }


  getUserData(){
    let user = this.auth.currentUser;
    return user;
  }


  getDomain(){
    return this.auth?.currentUser?.email?.split('@')[1];
  }

  isLoggedIn(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.auth.onAuthStateChanged(user => {
        if (user) {
          resolve(true);
        } else {
          resolve(false);
        }
      }, reject);
    });
  }

  private generateCodeVerifier(): string {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    const length = 128;
    let result = '';
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  }

  private async generateCodeChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    const base64Digest = this.base64URLEncode(new Uint8Array(digest));
    return base64Digest;
  }

  private base64URLEncode(buffer: Uint8Array): string {
    const base64 = btoa(String.fromCharCode.apply(null, [...buffer]));
    return base64
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  async initSalesforceAuth(): Promise<void> {
    // Hardcode localhost for development
    const redirectUri = 'http://localhost:4200/token';

    // Redirect to Salesforce auth URL with standard OAuth2 parameters
    const authUrl = `https://login.salesforce.com/services/oauth2/authorize?` +
      `client_id=3MVG9QV5z8XnLDvwr_cPCOAfy8y71AYIFPy3_aVpw5REe.wRXMOINt9bdp_ATaSSI82WUipJjbiKRLZU2HmkW&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `response_type=code`;

    window.location.href = authUrl;
  }

  async getToken(): Promise<string> {
    const user = await this.auth.currentUser;
    if (!user) throw new Error('No user logged in');
    return user.getIdToken();
  }

  /**
   * Re-proves the current password before a sensitive change (email update).
   * Firebase requires this for its own client-side email update calls, and
   * our backend independently checks the token's `auth_time` is recent.
   */
  async reauthenticateWithPassword(password: string): Promise<void> {
    const user = this.auth.currentUser;
    if (!user || !user.email) throw new Error('No user logged in');
    const credential = EmailAuthProvider.credential(user.email, password);
    await reauthenticateWithCredential(user, credential);
  }

  /**
   * Get onboarding prompt from localStorage (from self-service landing page)
   * Returns the prompt if valid and not expired, null otherwise
   */
  getOnboardingPrompt(): string | null {
    return this.getOnboardingData()?.prompt || null;
  }

  /**
   * Get full onboarding data (prompt + channels + directions) from localStorage
   */
  getOnboardingData(): { prompt: string; channels: string; directions: string } | null {
    try {
      const savedData = localStorage.getItem('meetzy_onboarding_prompt');
      if (!savedData) return null;

      const parsed = JSON.parse(savedData);

      if (parsed.expiresAt && parsed.expiresAt < Date.now()) {
        localStorage.removeItem('meetzy_onboarding_prompt');
        console.log('🚀 AuthService: Onboarding prompt expired, cleared from localStorage');
        return null;
      }

      console.log('🚀 AuthService: Found valid onboarding data');
      return {
        prompt: parsed.prompt || '',
        channels: parsed.channels || '',
        directions: parsed.directions || '',
      };
    } catch (error) {
      console.error('🚀 AuthService: Error reading onboarding data:', error);
      return null;
    }
  }

  /**
   * Clear the onboarding prompt from localStorage after it has been used
   */
  clearOnboardingPrompt(): void {
    try {
      localStorage.removeItem('meetzy_onboarding_prompt');
      console.log('🚀 AuthService: Onboarding prompt cleared from localStorage');
    } catch (error) {
      console.error('🚀 AuthService: Error clearing onboarding prompt:', error);
    }
  }
}
